// JFrog Artifactory bulk-import API client (v126).

import useSWR from 'swr';

export type ImportStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export type ImportPhase = 'pending' | 'listing' | 'transferring' | 'finishing' | 'done';
export type ImportSourceType = 'jfrog' | 'nexus' | 'github' | 'gitlab' | 'azure' | 'awscodeartifact' | 'gcparhybrid';
export type GitHubPackageType = '' | 'npm' | 'maven' | 'nuget' | 'rubygems' | 'container';

export interface ImportJob {
  id: string;
  name: string;
  source_type: ImportSourceType;
  source_url: string;
  target_project_name: string;
  repo_filter: string;
  dry_run: boolean;
  status: ImportStatus;
  phase: ImportPhase;
  error: string;
  repos_total: number;
  repos_done: number;
  artifacts_total: number;
  artifacts_done: number;
  bytes_total: number;
  bytes_done: number;
  dedup_hits: number;
  dedup_bytes: number;
  throughput_bps: number;
  worker_count: number;
  max_retries: number;
  effective_workers: number;
  rate_limit_hits: number;
  rate_limit_until?: string;
  started_at: string;
  finished_at?: string;
  started_by: string;
  cancel_requested: boolean;
}

export interface ImportRepo {
  src_key: string;
  src_format: string;
  src_kind: string;
  target_repo_id: string;
  artifacts_total: number;
  artifacts_done: number;
  bytes_done: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  error: string;
  started_at?: string;
  finished_at?: string;
}

export interface ImportDetail extends ImportJob {
  repos: ImportRepo[];
}

const fetcher = (url: string) =>
  fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useImports(sourceType?: ImportSourceType) {
  const url = sourceType
    ? `/api/admin/imports/jfrog?source_type=${sourceType}`
    : '/api/admin/imports/jfrog';
  return useSWR<ImportJob[]>(url, fetcher, {
    refreshInterval: 5_000,
  });
}

export function useImport(id: string | undefined) {
  return useSWR<ImportDetail>(id ? `/api/admin/imports/jfrog/${id}` : null, fetcher, {
    refreshInterval: 5_000,
  });
}

export async function startImport(input: {
  name?: string;
  source_type: ImportSourceType;
  source_url: string;
  api_token: string;
  target_project_name: string;
  repo_filter?: string;
  dry_run?: boolean;
  worker_count?: number;
  max_retries?: number;
  github_org?: string;
  github_package_type?: GitHubPackageType;
  gitlab_project_id?: string;
  gitlab_group_id?: string;
  gitlab_include_container?: boolean;
  azure_organization?: string;
  azure_project?: string;
  azure_feed?: string;
  aws_region?: string;
  aws_domain?: string;
  aws_domain_owner?: string;
  gcp_project_id?: string;
  gcp_location?: string;
  gcp_repository?: string;
}): Promise<{ id: string }> {
  const res = await fetch('/api/admin/imports/jfrog', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function cancelImport(id: string): Promise<void> {
  const res = await fetch(`/api/admin/imports/jfrog/${id}/cancel`, {
    method: 'POST', credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}

export async function retryFailed(id: string): Promise<void> {
  const res = await fetch(`/api/admin/imports/jfrog/${id}/retry-failed`, {
    method: 'POST', credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}

export interface ImportWorker {
  worker_id: string;
  hostname: string;
  current_artifact_id: number;
  current_path: string;
  current_size: number;
  bytes_so_far: number;
  started_at: string;
  last_heartbeat: string;
  stale: boolean;
}

export function useImportWorkers(id: string | undefined) {
  return useSWR<ImportWorker[]>(
    id ? `/api/admin/imports/jfrog/${id}/workers` : null,
    fetcher, { refreshInterval: 5_000 },
  );
}

export async function testConnection(input: {
  source_type: ImportSourceType;
  source_url: string;
  api_token: string;
  github_org?: string;
  github_package_type?: GitHubPackageType;
  gitlab_project_id?: string;
  gitlab_group_id?: string;
  gitlab_include_container?: boolean;
  azure_organization?: string;
  azure_project?: string;
  azure_feed?: string;
  aws_region?: string;
  aws_domain?: string;
  aws_domain_owner?: string;
  gcp_project_id?: string;
  gcp_location?: string;
  gcp_repository?: string;
}): Promise<{
  ok: boolean;
  repos_visible?: number;
  error?: string;
  warning?: string;
}> {
  const res = await fetch('/api/admin/imports/jfrog/test-connection', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
